import { useMemo } from 'react';
import { CommanderAPI } from '../CommanderAPI';
import useMyWorkspacesChildren from '../../../../redux/workspace/hooks/useMyWorkspacesChildren';
import useMultipleQueries from '../../../../shared/util/rtk/useQueries';
import { DeviceStatusSummaryAPIResponse } from '../deviceStatus.model';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../store/Store.constants';

type AggregatedResult = {
  isUninitialized: boolean;
  isFetching: boolean;
  currentData?: DeviceStatusSummaryAPIResponse;
  data?: DeviceStatusSummaryAPIResponse;
  error?: unknown;
};

const EMPTY_SUMMARY: DeviceStatusSummaryAPIResponse = {
  vpnDisconnected: 0,
  mqttDisconnected: 0,
  count: 0,
  mqttConnected: 0,
  vpnConnected: 0,
};

function useAggregatedGetDevicesConnectionStatusCountBySubWorkSpaces(
  enabled = true
): AggregatedResult {
  const { data: childWorkspacesDict } = useMyWorkspacesChildren({
    skip: !enabled,
  });

  const childWorkspaceIds = useMemo(
    () => (childWorkspacesDict ? Object.keys(childWorkspacesDict) : []),
    [childWorkspacesDict]
  );

  const childWorkspaceResponses = useMultipleQueries<
    DeviceStatusSummaryAPIResponse,
    string
  >(
    CommanderAPI.endpoints.getDevicesConnectionStatusCountByWorkspace,
    childWorkspaceIds,
    {
      pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
      refetchOnMountOrArgChange: true,
      skip: !childWorkspaceIds.length,
    }
  );

  const aggregatedCurrentData = useMemo<
    DeviceStatusSummaryAPIResponse | undefined
  >(() => {
    if (!enabled) {
      return EMPTY_SUMMARY;
    }

    if (!childWorkspaceIds.length) {
      return EMPTY_SUMMARY;
    }

    const summaries =
      childWorkspaceResponses.currentData ?? childWorkspaceResponses.data;
    if (!summaries) {
      return undefined;
    }

    return summaries.reduce<DeviceStatusSummaryAPIResponse>(
      (accumulator, summary) => ({
        vpnDisconnected:
          accumulator.vpnDisconnected + (summary?.vpnDisconnected ?? 0),
        mqttDisconnected:
          accumulator.mqttDisconnected + (summary?.mqttDisconnected ?? 0),
        count: accumulator.count + (summary?.count ?? 0),
        mqttConnected:
          accumulator.mqttConnected + (summary?.mqttConnected ?? 0),
        vpnConnected: accumulator.vpnConnected + (summary?.vpnConnected ?? 0),
      }),
      {
        ...EMPTY_SUMMARY,
      }
    );
  }, [
    childWorkspaceIds.length,
    childWorkspaceResponses.currentData,
    childWorkspaceResponses.data,
    enabled,
  ]);

  return useMemo(
    () => ({
      ...childWorkspaceResponses,
      currentData: aggregatedCurrentData,
      data: aggregatedCurrentData,
      isUninitialized: !enabled || childWorkspaceResponses.isUninitialized,
    }),
    [aggregatedCurrentData, childWorkspaceResponses, enabled]
  );
}

export default useAggregatedGetDevicesConnectionStatusCountBySubWorkSpaces;
